import { FlexBox, Text } from '@dixa/lib-shared-habla';
import { colors, typography } from '@dixa/lib-shared-tokens';
import classnames from 'classnames';
import { Cell, Pie, PieChart } from 'recharts';

import {
  DashboardQueue,
  DashboardQueueConversationsCounts,
} from '../../../../api/types';

import styles from './conversations.module.scss';

type ConversationsProps = {
  dashboardQueue: DashboardQueue;
};

type LegendItem = {
  type: keyof DashboardQueueConversationsCounts;
  name: string;
  color: string;
  shape: 'circle' | 'square';
};

const LEGEND_CONFIG: ReadonlyArray<LegendItem> = [
  {
    type: 'total_conversations',
    name: 'Total',
    color: colors.neutral[400],
    shape: 'square',
  },
  {
    type: 'answered_conversations',
    name: 'Answered',
    color: colors.green[400],
    shape: 'circle',
  },
  {
    type: 'transfered_conversations',
    name: 'Timeouts',
    color: colors.blue[400],
    shape: 'circle',
  },
  {
    type: 'abandoned_conversations',
    name: 'Abandoned',
    color: colors.red[400],
    shape: 'circle',
  },
];

type ChartEntry = {
  type: keyof DashboardQueueConversationsCounts;
  color: string;
  value?: number;
};

const CHART_CONFIG: ReadonlyArray<ChartEntry> = [
  {
    type: 'answered_conversations',
    color: colors.green[400],
  },
  {
    type: 'conversations_in_queue',
    color: colors.orange[400],
  },
  {
    type: 'transfered_conversations',
    color: colors.blue[400],
  },
  {
    type: 'abandoned_conversations',
    color: colors.red[400],
  },
];

const DIAMETER = 72;
const STROKE = 8;

export const Conversations = ({
  dashboardQueue,
}: ConversationsProps): JSX.Element => {
  const total = dashboardQueue.conversations_in_queue ?? 0;

  const data = CHART_CONFIG.map(({ type, color }) => ({
    value: dashboardQueue[type] ?? 0,
    color,
  })).filter((item) => !!item.value);

  return (
    <FlexBox alignItems="center" gap="400">
      <PieChart width={DIAMETER} height={DIAMETER}>
        <Pie
          data={data.length ? data : [{ value: 1 }]} // Dummy value to render a full empty circle
          dataKey="value"
          innerRadius={DIAMETER / 2 - STROKE}
          outerRadius={DIAMETER / 2}
          startAngle={90} // Start at the 12:00 position
          endAngle={-270} // End at the 12:00 position
          fill={colors.neutral[300]}
          stroke="none"
          isAnimationActive={false}
        >
          {data.map((cell, index) => (
            <Cell key={`cell-${index}`} fill={cell.color} strokeWidth={0} />
          ))}
        </Pie>

        <text
          x="50%"
          y="50%"
          dy={parseInt(typography.size[400], 10) / 2 - 1}
          fill={total > 0 ? colors.orange[400] : colors.neutral[300]}
          fontSize={typography.size[400]}
          fontWeight={typography.weight.semibold}
          textAnchor="middle"
        >
          {total}
        </text>
      </PieChart>

      <ul className={styles.legend}>
        {LEGEND_CONFIG.map(({ name, color, type, shape }) => (
          <li key={type} className={styles.legendItem}>
            <div
              className={classnames(
                { [styles[shape]]: true },
                styles.legendIcon,
              )}
              style={{ backgroundColor: color }}
            />

            <Text.Paragraph variant="4" className={styles.legendLabel}>
              {name}
            </Text.Paragraph>

            <Text.Paragraph variant="1" className={styles.legendCount}>
              {dashboardQueue[type] || 0}
            </Text.Paragraph>
          </li>
        ))}
      </ul>
    </FlexBox>
  );
};
